<template>
  <div class="flex flex-col gap-3">
    <AppointmentCard
      :appointment-number="appointment.id"
      :created-date="formatDateWithTimezone(appointment.created_time)"
      :scheduled-start-date="formatDateWithTimezone(appointment.scheduled_time)"
      :scheduled-start-time="
        formatTimeWithTimezone(appointment.arrival_window_start)
      "
      :scheduled-end="formatTimeWithTimezone(appointment.arrival_window_end)"
      :estimated-duration="
        formatDuration(appointment.estimated_duration_minutes)
      "
      :customer="appointment.customer"
      :job="appointment.job"
      :location-line1="appointment.address.name"
      :location-line2="formatLocationLine2(appointment.address)"
      :created-by="appointment.created_by?.full_name"
      :member-since-date="appointment.customer.membership?.starts_at"
      :expire-date="appointment.customer.membership?.ends_at"
      :customer-tags="appointment.customer.tags || []"
      :job-tags="appointment.job.tags || []"
      :duration-editable="appointment.status === 'pending'"
      :scheduled-time-editable="
        ['pending', 'completed', 'running'].includes(appointment.status)
      "
      :priority="appointment.service_category.priority"
      :permit-status="appointment.job.permits[0]?.status"
      :permit-path="permitPath(appointment.job)"
      :appointment-count="appointment.job.appointment_count"
      @refresh-appointment="emit('refresh-appointment')"
    >
      <template #estimatedDurationTooltip>
        <EstimatedDurationForm
          :appointment="appointment"
          @saved="emit('refresh-appointment')"
        />
      </template>
      <template #scheduledTimeTooltip>
        <ScheduledTimeForm
          :appointment="appointment"
          :backdate="appointment.status !== 'pending'"
          @request-save="(data) => emit('request-save', data)"
        />
      </template>
    </AppointmentCard>
    <PinnedNotes :pinned-notes="appointment.pinned_notes" />
    <AppointmentTimes
      :appointment="appointment"
      @refresh-appointment="emit('refresh-appointment')"
    />
  </div>
</template>

<script setup lang="ts">
import AppointmentCard from "@/components/Seranova/AppointmentCard/AppointmentCard.vue";
import { formatDateWithTimezone } from "@/mixins";
import { AppointmentV2 } from "@/typings/appointment";
import AppointmentTimes from "./AppointmentTimes.vue";
import EstimatedDurationForm from "./EstimatedDurationForm.vue";
import PinnedNotes from "./PinnedNotes.vue";
import ScheduledTimeForm from "./ScheduledTimeForm.vue";

const emit = defineEmits(["refresh-appointment", "request-save"]);
defineProps<{ appointment: AppointmentV2 }>();

// The permit label links to the permit modal (Rails UJS data-remote), matching
// Job/Header/JobDetails: an existing permit opens to edit, none opens create
// for this job.
const permitPath = (job: AppointmentV2["job"]): string => {
  const permit = job.permits[0];
  return permit
    ? `/permits/${permit.id}`
    : `/permits/new?permit%5Bjob_id%5D=${job.id}`;
};

const formatLocationLine2 = (address: AppointmentV2["address"]) => {
  if (!address) return "undefined";
  const city = address.city || "";
  const state = address.state || "";
  const zip = address.zip_code || "";
  if (!city && !state && !zip) return "undefined";
  const stateZip = [state, zip].filter(Boolean).join(" ");
  const line2 = [city, stateZip].filter(Boolean).join(", ");
  return line2 || "undefined";
};
</script>

<style scoped></style>
